<template>
  <div
    id="offcanvasCheckBill"
    class="offcanvas offcanvas-fullscreen-sm-down offcanvas-end"
    tabindex="-1"
    ref="offcanvas"
    data-bs-backdrop="static"
    data-bs-scroll="true"
    :style="{ width: offcanvasWidth, maxWidth: '100%', transition: 'width 0.2s ease' }"
  >
    <!-- Persistent Header -->
    <div class="offcanvas-header bg-success text-white py-3">
      <div>
        <h5 class="offcanvas-title fw-bold mb-0">
          <i :class="headerIcon" class="me-2" />
          {{ $t(headerTitle) }}
        </h5>
        <div class="small opacity-75 mt-1">
          {{ sessionName }}
          <template v-if="currentView === 'bill' && bills.length > 1">
            &nbsp;|&nbsp; {{ $t('Split into') }} {{ bills.length }} {{ $t('bills') }}
          </template>
          <template v-else-if="currentView === 'merge'">
            &nbsp;|&nbsp; {{ $t('Combine multiple bills into one') }}
          </template>
          <template v-else-if="currentView !== 'bill'">
            &nbsp;|&nbsp; {{ activeBill.bill_no }}
          </template>
        </div>
      </div>
      <button type="button" class="btn-close btn-close-white" data-bs-dismiss="offcanvas" />
    </div>

    <template v-if="currentView === 'bill'">
    <!-- Loading state -->
    <div v-if="loading" class="offcanvas-body p-3" style="background: #f7f9fc;">
      <p-o-s-page-skeleton variant="bills" :show-header="false" />
    </div>

    <template v-else>
    <!-- Bill Tabs (when split) -->
    <div v-if="bills.length > 1" class="p-3 bg-light border-bottom">
      <div class="d-flex gap-1 flex-wrap">
        <button
          v-for="(bill, idx) in bills"
          :key="bill.id"
          type="button"
          class="btn btn-sm rounded-3 px-3 d-flex align-items-center gap-1"
          :class="idx === activeBillIndex ? 'btn-success text-white' : 'btn-outline-secondary'"
          @click="activeBillIndex = idx"
        >
          <span class="fw-semibold">{{ $t('Bill') }} {{ idx + 1 }}</span>
          <span
            v-if="getBillState(bill).kind !== 'unpaid'"
            class="badge ms-1"
            :class="'bg-' + getBillState(bill).color"
            style="font-size: 0.65rem;"
            :title="$t(getBillState(bill).label)"
          >
            <i :class="'fa-solid ' + getBillState(bill).icon"></i>
          </span>
          <span class="small opacity-75 ms-1">{{ bill.items.length }}</span>
        </button>
      </div>
    </div>

    <!-- Body -->
    <div class="offcanvas-body p-3" style="background: #f7f9fc;">

      <!-- Status banner — shown when the bill is voided, refunded, or paid -->
      <div
        v-if="activeBillState.kind !== 'unpaid' && activeBillState.kind !== 'partial_paid'"
        class="rounded-3 p-3 mb-3 d-flex align-items-start gap-2 shadow-sm"
        :class="'bg-' + activeBillState.color + '-subtle border border-' + activeBillState.color + '-subtle text-' + activeBillState.color"
      >
        <i :class="'fa-solid ' + activeBillState.icon + ' mt-1'" style="font-size: 1.1rem;"></i>
        <div class="flex-grow-1" style="font-size: 0.85rem;">
          <div class="fw-bold">{{ $t(activeBillState.label) }}</div>
          <div v-if="activeBillState.kind === 'void' && activeBill.void_reason" class="opacity-75 small">
            {{ activeBill.void_reason }}
          </div>
          <div
            v-else-if="(activeBillState.kind === 'refunded' || activeBillState.kind === 'partial_refund') && activeBill.refund_reason"
            class="opacity-75 small"
          >
            {{ activeBill.refund_reason }}
          </div>
          <div
            v-if="activeBillState.kind === 'partial_refund' && activeBill.refunded_amount > 0"
            class="opacity-75 small"
          >
            {{ $t('Refunded') }}:
            {{ $storeIndex.formatThaiCurrency(activeBill.refunded_amount) }}
            / {{ $storeIndex.formatThaiCurrency(activeBill.grand_total) }}
          </div>
        </div>
      </div>

      <!-- Bill Info Chips -->
      <div class="d-flex flex-wrap gap-2 mb-3">
        <div class="bg-white rounded-3 px-3 py-2 shadow-sm flex-fill" style="min-width: 140px;">
          <div class="text-muted" style="font-size: 0.7rem;">{{ $t('Bill No') }}</div>
          <div class="fw-semibold" style="font-size: 0.85rem;">{{ activeBill.bill_no || '-' }}</div>
        </div>
        <div class="bg-white rounded-3 px-3 py-2 shadow-sm flex-fill" style="min-width: 100px;">
          <div class="text-muted" style="font-size: 0.7rem;">{{ $t('Cashier') }}</div>
          <div class="fw-semibold" style="font-size: 0.85rem;">{{ activeBill.created_by || 'System' }}</div>
        </div>
        <div class="bg-white rounded-3 px-3 py-2 shadow-sm" style="min-width: 80px;">
          <div class="text-muted" style="font-size: 0.7rem;">{{ $t('Guests') }}</div>
          <div class="fw-semibold" style="font-size: 0.85rem;">
            <i class="fa-solid fa-user me-1 text-muted" style="font-size: 0.7rem;"></i>{{ activeBill.guests }}
          </div>
        </div>
      </div>

      <!-- Items List -->
      <div class="mb-3">
        <div class="d-flex align-items-center justify-content-between mb-2 px-1">
          <span class="fw-bold text-dark">
            <i class="fa-solid fa-utensils me-2 text-muted"></i>{{ $t('Order Items') }}
          </span>
          <span class="badge bg-secondary-subtle text-secondary rounded-pill">
            {{ activeBill.items.filter(i => !i.cancelled).length }} {{ $t('items') }}
          </span>
        </div>

        <div v-if="activeBill.items.length === 0" class="text-center text-muted py-4">
          <i class="fa-solid fa-utensils fa-2x opacity-25 mb-2"></i>
          <p class="small mb-0">{{ $t('No items in this bill') }}</p>
        </div>

        <div
          v-for="(item, index) in activeBill.items"
          :key="index"
          class="bg-white rounded-3 p-3 mb-2 shadow-sm"
          :class="{ 'opacity-50': item.cancelled }"
        >
          <!-- Header row: qty + name pinned with line total on the right -->
          <div class="d-flex align-items-start gap-2">
            <span class="badge bg-success-subtle text-success rounded-3 fw-bold flex-shrink-0" style="min-width: 32px; font-size: 0.85rem;">
              {{ item.quantity }}x
            </span>
            <div class="d-flex flex-wrap align-items-baseline gap-1" style="flex: 1; min-width: 0;">
              <span class="fw-semibold" :class="{ 'text-decoration-line-through': item.cancelled }">
                {{ $t(item.name) }}
              </span>
              <span
                v-if="item.is_reward"
                class="badge bg-warning text-dark"
                style="font-size: 0.6rem;"
              >
                <i class="fa-solid fa-gift me-1"></i>{{ $t('Reward') }}
              </span>
              <span v-if="!item.is_reward" class="text-muted small" :class="{ 'text-decoration-line-through': item.cancelled }">
                @{{ $storeIndex.formatThaiCurrency(item.base_price) }}
              </span>
              <span
                v-if="isItemVatable(item)"
                class="badge bg-info-subtle text-info border border-info-subtle"
                style="font-size: 0.6rem;"
                :title="$t('VAT')"
              >
                <i class="fa-solid fa-percent me-1"></i>{{ $t('VAT') }}
              </span>
              <span
                v-if="item.cancelled"
                class="badge bg-danger-subtle text-danger border border-danger-subtle"
                style="font-size: 0.6rem;"
                :title="item.cancelled_reason"
              >
                <i class="fa-solid fa-circle-xmark me-1"></i>{{ $t('Cancelled') }}
              </span>
              <span
                v-if="item.is_refunded || item.refunded_quantity >= item.quantity && item.refunded_quantity > 0"
                class="badge bg-warning-subtle text-warning border border-warning-subtle"
                style="font-size: 0.6rem;"
                :title="item.refund_reason"
              >
                <i class="fa-solid fa-rotate-left me-1"></i>{{ $t('Refunded') }}
              </span>
            </div>
            <div class="text-end flex-shrink-0" style="white-space: nowrap;">
              <div class="fw-bold" :class="{ 'text-decoration-line-through text-muted': item.cancelled }">
                {{ $storeIndex.formatThaiCurrency(item.total) }}
              </div>
              <div v-if="item.discount > 0" class="text-danger" style="font-size: 0.7rem; line-height: 1;">
                -{{ $storeIndex.formatThaiCurrency(item.discount) }}
              </div>
            </div>
          </div>

          <!-- Detail section: indented under the qty badge -->
          <div style="padding-left: 40px;">
            <!-- Cancellation chip — shown for both partial and full cancellations -->
            <div
              v-if="item.cancelled_quantity > 0"
              class="d-flex align-items-center gap-2 bg-danger-subtle text-danger border border-danger-subtle rounded-2 px-2 py-1 mt-2"
              style="font-size: 0.7rem;"
            >
              <i class="fa-solid fa-circle-xmark"></i>
              <div class="flex-grow-1">
                <span class="fw-semibold">
                  {{ $t('Cancelled') }} {{ formatCancelledQty(item.cancelled_quantity) }}/{{ item.quantity }}
                </span>
                <template v-if="item.cancelled_reason">
                  <span class="opacity-75"> · {{ item.cancelled_reason }}</span>
                </template>
              </div>
            </div>
            <!-- Refund chip — partial or full -->
            <div
              v-if="item.refunded_quantity > 0"
              class="d-flex align-items-center gap-2 bg-warning-subtle text-warning border border-warning-subtle rounded-2 px-2 py-1 mt-2"
              style="font-size: 0.7rem;"
            >
              <i class="fa-solid fa-rotate-left"></i>
              <div class="flex-grow-1">
                <span class="fw-semibold">
                  {{ $t('Refunded') }} {{ formatCancelledQty(item.refunded_quantity) }}/{{ item.quantity }}
                </span>
                <span v-if="item.refunded_amount > 0" class="opacity-75">
                  · -{{ $storeIndex.formatThaiCurrency(item.refunded_amount) }}
                </span>
                <template v-if="item.refund_reason">
                  <span class="opacity-75"> · {{ item.refund_reason }}</span>
                </template>
              </div>
            </div>
            <div v-if="item.modifiers && item.modifiers.length > 0" class="mt-2 ps-2 border-start border-2 border-light-subtle">
              <div
                v-for="mod in item.modifiers"
                :key="mod.name"
                class="d-flex justify-content-between align-items-center small"
                style="font-size: 0.75rem;"
              >
                <span class="text-muted">
                  <i class="fa-solid fa-angle-right me-1 opacity-50"></i>{{ $t(mod.name) }}
                </span>
                <span v-if="mod.price > 0" class="text-theme fw-semibold ms-2">
                  +{{ $storeIndex.formatThaiCurrency(mod.price) }}
                </span>
              </div>
            </div>
            <div v-if="item.remark" class="text-muted small mt-1">
              <i class="fa-regular fa-comment me-1"></i>{{ item.remark }}
            </div>
            <!-- Per-item discount input — ของแถมไม่ให้ลดราคาฐานซ้ำ (ฐานฟรีอยู่แล้ว) -->
            <div v-if="!item.cancelled && !activeBillFinalized && !item.is_reward" class="d-flex align-items-center gap-2 mt-2">
              <span class="text-muted small" style="font-size: 0.75rem;">
                <i class="fa-solid fa-tag me-1"></i>{{ $t('Discount') }}
              </span>
              <div class="input-group input-group-sm" style="max-width: 130px;">
                <input
                  type="number"
                  class="form-control form-control-sm text-end"
                  min="0"
                  step="0.01"
                  :max="item.subtotal"
                  :placeholder="'0.00'"
                  :value="item.discount || ''"
                  @input="onItemDiscountInput(item, $event.target.value)"
                />
                <span class="input-group-text">฿</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Totals Card -->
      <div class="bg-white rounded-3 p-3 shadow-sm mb-3">
        <div class="d-flex justify-content-between mb-2">
          <span class="text-muted">{{ $t('Subtotal') }}</span>
          <span class="fw-semibold">{{ $storeIndex.formatThaiCurrency(activeBill.subtotal) }}</span>
        </div>

        <!-- Bill-level Discount input -->
        <div class="d-flex justify-content-between align-items-center mb-2">
          <span class="text-danger">
            <i class="fa-solid fa-tag me-1"></i>{{ $t('Discount') }}
          </span>
          <div class="input-group input-group-sm" style="max-width: 150px;">
            <input
              type="number"
              class="form-control form-control-sm text-end"
              min="0"
              step="0.01"
              placeholder="0.00"
              :disabled="activeBillFinalized"
              :value="activeBill.discount || ''"
              @input="onBillDiscountInput($event.target.value)"
            />
            <span class="input-group-text">฿</span>
          </div>
        </div>

        <!-- Shipping Price input -->
        <div class="d-flex justify-content-between align-items-center mb-2">
          <span class="text-muted">
            <i class="fa-solid fa-truck me-1"></i>{{ $t('Shipping') }}
          </span>
          <div class="input-group input-group-sm" style="max-width: 150px;">
            <input
              type="number"
              class="form-control form-control-sm text-end"
              min="0"
              step="0.01"
              placeholder="0.00"
              :disabled="activeBillFinalized"
              :value="activeBill.shipping_price || ''"
              @input="onShippingPriceInput($event.target.value)"
            />
            <span class="input-group-text">฿</span>
          </div>
        </div>

        <div v-if="activeBill.service_charge > 0" class="d-flex justify-content-between mb-2">
          <span class="text-muted">{{ $t('Service Charge') }} ({{ activeBill.service_charge_rate }}%)</span>
          <span class="fw-semibold">{{ $storeIndex.formatThaiCurrency(activeBill.service_charge) }}</span>
        </div>
        <div v-if="activeBill.vat > 0" class="d-flex justify-content-between">
          <span class="text-muted">
            {{ $t('VAT') }} ({{ activeBill.vat_rate }}%)
            <span v-if="activeBill.tax_policy === 'Inclusive'" class="text-muted small">{{ $t('included') }}</span>
          </span>
          <span class="fw-semibold">{{ $storeIndex.formatThaiCurrency(activeBill.vat) }}</span>
        </div>
      </div>

      <!-- Attach / เปลี่ยน / เอาออก member — แสดงเสมอ (ผูกแล้วก็เปลี่ยนได้ เช่น แยกบิลแล้วผูกสมาชิกคนละคน) -->
      <div
        v-if="!String(activeBill.id).startsWith('local-') && !activeBillFinalized"
        class="mb-3"
      >
        <label class="form-label small fw-bold text-muted mb-2">
          <i class="fa-solid fa-id-card me-1"/>{{ $t('Attach Member') }}
        </label>
        <!-- สมาชิกที่ผูกอยู่ตอนนี้ — การ์ด avatar + ชื่อ + ปุ่มเอาออก -->
        <transition name="member-fade">
          <div
            v-if="activeBill.customer_name"
            class="member-card d-flex align-items-center gap-2 p-2 mb-2 rounded-3"
          >
            <div class="member-avatar d-flex align-items-center justify-content-center rounded-circle flex-shrink-0">
              <i class="fa-solid fa-user"/>
            </div>
            <div class="flex-grow-1" style="min-width: 0;">
              <div class="fw-bold text-success text-truncate">{{ activeBill.customer_name }}</div>
              <div class="member-sub d-flex align-items-center">
                <i class="fa-solid fa-circle-check me-1"/>{{ $t('Member of this bill') }}
              </div>
            </div>
            <button
              type="button"
              class="member-remove btn btn-sm d-flex align-items-center justify-content-center rounded-circle flex-shrink-0 p-0"
              :title="$t('Remove')"
              @click="onDetachMember"
            >
              <i class="fa-solid fa-xmark"/>
            </button>
          </div>
        </transition>
        <!-- ค้นหาเลือกสมาชิก (ผูกแล้วเลือกใหม่ทับได้ = เปลี่ยนสมาชิก) -->
        <member-select
          :dropdown-parent="'#offcanvasCheckBill'"
          :placeholder="activeBill.customer_name ? ($t('Change member') + '...') : ''"
          @selected="onAttachMember"
          @cleared="onDetachMember"
        />
      </div>

      <!-- Loyalty (balances / earn preview / redeem) — only when the bill has a customer -->
      <loyalty-bill-panel
        v-if="activeBill.customer_name && !String(activeBill.id).startsWith('local-')"
        :bill-id="activeBill.id"
        :finalized="activeBillFinalized"
        :loyalty="activeBill.loyalty"
        @changed="onRewardChanged"
      />
      <!-- ยังไม่มีบิลจริง แต่ session ผูกสมาชิก → แลกได้เลย (BE สร้างบิลให้) -->
      <loyalty-bill-panel
        v-else-if="sessionLoyalty && sessionLoyalty.has_customer"
        :bill-id="0"
        :loyalty="sessionLoyalty"
        :session-token="sessionToken"
        @changed="onRewardChanged"
      />

      <!-- โปรซื้อครบ (GWP เติมอัตโนมัติ / PWP รับสิทธิ์) — โชว์ได้แม้ walk-in (ไม่ต้องมีลูกค้า) -->
      <promotion-bill-panel
        v-if="!String(activeBill.id).startsWith('local-')"
        :bill-id="activeBill.id"
        :finalized="activeBillFinalized"
        @changed="onRewardChanged"
      />

      <!-- Grand Total Highlight -->
      <div
        class="rounded-3 p-3 text-white d-flex justify-content-between align-items-center"
        :style="grandTotalBannerStyle"
      >
        <div>
          <div class="opacity-75" style="font-size: 0.75rem;">
            {{ $t(activeBillState.kind === 'unpaid' || activeBillState.kind === 'partial_paid' ? 'Grand Total' : activeBillState.label) }}
          </div>
          <div class="fw-bold" style="font-size: 1.5rem;">{{ $storeIndex.formatThaiCurrency(activeBill.grand_total) }}</div>
        </div>
        <i
          class="opacity-50"
          :class="'fa-solid ' + (activeBillFinalized ? activeBillState.icon : 'fa-receipt')"
          style="font-size: 2rem;"
        ></i>
      </div>

    </div>

    <!-- Footer -->
    <div class="bg-white p-3 border-top d-flex flex-wrap gap-2">
      <button
        type="button"
        class="btn btn-outline-primary rounded-3 flex-fill"
        :disabled="printing || activeBill.items.length === 0"
        @click="handlePrint()"
      >
        <i class="fa-solid fa-spinner fa-spin me-1" v-if="printing"></i>
        <i class="fa-solid fa-print me-1" v-else></i> {{ $t('Print') }}
      </button>
      <button
        v-if="!activeBillFinalized && activeBillSplittableUnits > 1"
        type="button"
        class="btn btn-outline-warning rounded-3 flex-fill"
        @click="currentView = 'split'"
      >
        <i class="fa-solid fa-object-ungroup me-1"></i> {{ $t('Split Bill') }}
      </button>
      <button
        v-if="bills.length > 1 && unpaidBillsCount >= 2"
        type="button"
        class="btn btn-outline-info rounded-3 flex-fill"
        @click="currentView = 'merge'"
      >
        <i class="fa-solid fa-object-group me-1"></i> {{ $t('Merge Bill') }}
      </button>
      <button
        v-if="activeBill.grand_total > 0"
        type="button"
        class="btn btn-success rounded-3 flex-fill fw-bold"
        :disabled="activeBillFinalized"
        @click="currentView = 'payment'"
      >
        <i class="fa-solid fa-cash-register me-1"></i> {{ $t('Payment') }}
      </button>
      <!-- บิลยอด ฿0 (เช่น แลกของแถมล้วน) → ปิดบิลได้โดยไม่ต้องรับเงิน -->
      <button
        v-else
        type="button"
        class="btn btn-success rounded-3 flex-fill fw-bold"
        :disabled="activeBillFinalized || paying || !hasBillableItems || String(activeBill.id).startsWith('local-')"
        @click="closeFreeBill"
      >
        <i class="fa-solid fa-check-circle me-1"></i> {{ $t('Close Bill') }} (฿0)
      </button>
    </div>
    </template>
    </template>

    <!-- Inline: Payment -->
    <offcanvas-payment
      v-else-if="currentView === 'payment'"
      inline
      :session-name="sessionName"
      :bill="activeBill"
      :payment-methods="paymentMethods"
      :session-token="sessionToken"
      :submitting="paying"
      @back="currentView = 'bill'"
      @confirmed="onPaymentConfirmed"
    />

    <!-- Inline: Split Bill -->
    <offcanvas-split-bill
      v-else-if="currentView === 'split'"
      inline
      ref="splitBillRef"
      :session-name="sessionName"
      :items="activeBill.items"
      :submitting="splitting"
      @back="currentView = 'bill'"
      @confirmed="onSplitConfirmed"
    />

    <!-- Inline: Merge Bill -->
    <offcanvas-merge-bill
      v-else-if="currentView === 'merge'"
      inline
      :session-name="sessionName"
      :bills="bills"
      :default-target-id="activeBill.id"
      :submitting="merging"
      @back="currentView = 'bill'"
      @confirmed="onMergeConfirmed"
    />
  </div>
</template>

<script>
import {
  buildPromptPayQrImage,
  buildReviewQrImage,
  buildReceiptDocDefinition,
  preparePrintConfig,
  printPdf
} from '@/utils/billReceipt';
import { isSupported as escposIsSupported, printCanvas, renderDocDefToCanvas, dotsForPaperWidth, openCashDrawer } from '@/utils/escposPrinter';
import { Sessions } from '@/services/endpoint/pos.service';
import MemberSelect from '@/components/shared/MemberSelect.vue';
import { Bills } from '@/services/endpoint/bills.service';
import POSPageSkeleton from '@/plugins/POSPageSkeleton.vue';
import OffcanvasPayment from './OffcanvasPayment.vue';
import OffcanvasSplitBill from './OffcanvasSplitBill.vue';
import OffcanvasMergeBill from './OffcanvasMergeBill.vue';
import LoyaltyBillPanel from '@/components/pos/loyalty/LoyaltyBillPanel.vue';
import PromotionBillPanel from '@/components/pos/promotion/PromotionBillPanel.vue';
import { useScrollRestore } from '@/composables/useScrollRestore';

export default {
  name: 'OffcanvasCheckBill',
  components: { POSPageSkeleton, OffcanvasPayment, OffcanvasSplitBill, OffcanvasMergeBill, LoyaltyBillPanel, PromotionBillPanel, MemberSelect },
  props: {
    sessionName: {
      type: String,
      default: ''
    },
    sessionToken: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      loading: false,
      // แต้มระดับ session (โชว์+แลกได้ตอนยังไม่มีบิลจริง)
      sessionLoyalty: null,
      printing: false,
      splitting: false,
      merging: false,
      paying: false,
      currentView: 'bill',
      bills: [],
      // Branch-scoped payment methods for the active session — returned alongside
      // bills by GET /sessions/{token}/bills. Drives the Payment offcanvas grid.
      paymentMethods: [],
      activeBillIndex: 0,
      localBillCounter: 0,
    };
  },
  computed: {
    offcanvasWidth() {
      return this.currentView === 'bill' ? '560px' : '820px';
    },
    headerTitle() {
      if (this.currentView === 'payment') return 'Payment';
      if (this.currentView === 'split') return 'Split Bill';
      if (this.currentView === 'merge') return 'Merge Bill';
      return 'Check Bill';
    },
    headerIcon() {
      if (this.currentView === 'payment') return 'fa-solid fa-cash-register';
      if (this.currentView === 'split') return 'fa-solid fa-object-ungroup';
      if (this.currentView === 'merge') return 'fa-solid fa-object-group';
      return 'fa-solid fa-file-invoice-dollar';
    },
    unpaidBillsCount() {
      return this.bills.filter(b => !this.isBillFinalized(b)).length;
    },
    // จำนวน "ชิ้น" ที่ยังย้ายได้ (ไม่นับที่ถูกยกเลิก) — split ได้เมื่อมี > 1 ชิ้น
    // นับเป็นชิ้น ไม่ใช่บรรทัด: บรรทัดเดียว qty 2 ก็แยกได้ (ย้าย 1 เหลือ 1)
    activeBillSplittableUnits() {
      return (this.activeBill?.items || [])
        .filter(i => !i.cancelled)
        .reduce((sum, i) => sum + Math.max(0, (parseInt(i.quantity) || 0) - (parseInt(i.cancelled_quantity) || 0)), 0);
    },
    activeBill() {
      return this.bills[this.activeBillIndex] || this.emptyBill();
    },
    activeBillState() {
      return this.getBillState(this.activeBill);
    },
    activeBillFinalized() {
      return this.isBillFinalized(this.activeBill);
    },
    // มีรายการที่ยังขายอยู่จริง (ไม่ถูกยกเลิก) — ใช้ตัดสินว่าปิดบิล ฿0 ได้ไหม
    hasBillableItems() {
      return (this.activeBill.items || []).some(i => !i.cancelled);
    },
    grandTotalBannerStyle() {
      const grad = {
        unpaid:         'linear-gradient(135deg, #198754 0%, #20c997 100%)',
        partial_paid:   'linear-gradient(135deg, #0dcaf0 0%, #6f42c1 100%)',
        paid:           'linear-gradient(135deg, #6c757d 0%, #495057 100%)',
        partial_refund: 'linear-gradient(135deg, #ffc107 0%, #fd7e14 100%)',
        refunded:       'linear-gradient(135deg, #dc3545 0%, #6f1d1b 100%)',
        void:           'linear-gradient(135deg, #6c757d 0%, #343a40 100%)'
      };
      return 'background: ' + (grad[this.activeBillState.kind] || grad.unpaid) + ';';
    },
  },
  methods: {
    // Derive a UI-facing state object from the bill's payment/refund/void fields.
    // `kind` drives gating (isBillFinalized); the rest controls badges + banner colors.
    getBillState(bill) {
      if (!bill) return { kind: 'unpaid', label: 'Unpaid', color: 'primary', icon: 'fa-clock' };
      if (bill.voided_at || bill.payment_status === 'voided') {
        return { kind: 'void', label: 'Voided', color: 'secondary', icon: 'fa-ban' };
      }
      if (bill.payment_status === 'refunded') {
        return { kind: 'refunded', label: 'Refunded', color: 'danger', icon: 'fa-rotate-left' };
      }
      if (bill.payment_status === 'partially_refunded') {
        return { kind: 'partial_refund', label: 'Partially Refunded', color: 'warning', icon: 'fa-rotate-left' };
      }
      if (bill.payment_status === 'paid' || bill.paid) {
        return { kind: 'paid', label: 'Paid', color: 'success', icon: 'fa-circle-check' };
      }
      if (bill.payment_status === 'partially_paid') {
        return { kind: 'partial_paid', label: 'Partially Paid', color: 'info', icon: 'fa-coins' };
      }
      return { kind: 'unpaid', label: 'Unpaid', color: 'primary', icon: 'fa-clock' };
    },
    isBillFinalized(bill) {
      const k = this.getBillState(bill).kind;
      return k === 'paid' || k === 'refunded' || k === 'partial_refund' || k === 'void';
    },
    emptyBill() {
      return {
        id: 0,
        bill_no: '',
        items: [],
        guests: 0,
        subtotal: 0,
        discount: 0,
        service_charge: 0,
        service_charge_rate: 0,
        vat: 0,
        vat_rate: 0,
        vatable_total: 0,
        exempt_total: 0,
        shipping_price: 0,
        tax_policy: 'No Tax',
        grand_total: 0,
        // บิลที่ยังไม่ได้เปิดจริง = ยังไม่จ่ายแน่นอน → ใบที่พิมพ์เป็นใบแจ้งรายการ
        payment_status: 'unpaid',
        paid: false,
        created_by: '',
      };
    },
    // --- UI helpers / input handlers ---
    isItemVatable(item) {
      if (!item || item.tax_status !== 'Vatable') return false;
      if (this.activeBill.tax_policy === 'No Tax') return false;
      return this.activeBill.vat_rate > 0;
    },
    // API sends cancelled_quantity as a decimal string (e.g. "1.00") — show whole units
    // when the value is a whole number, keep the fractional form otherwise.
    formatCancelledQty(qty) {
      const n = parseFloat(qty) || 0;
      return Number.isInteger(n) ? String(n) : n.toFixed(2);
    },
    onItemDiscountInput(item, raw) {
      // Subtotal from API already excludes the cancelled portion, so cap discount there.
      const max = item.subtotal || 0;
      const value = Math.max(0, Math.min(max, parseFloat(raw) || 0));
      item.discount = value;
      this.recomputeBillTotals(this.activeBill);
    },
    onBillDiscountInput(raw) {
      /**
       * จำกัดไม่ให้เกินยอดสินค้าหลังหักส่วนลดรายบรรทัด — เพดานเดียวกับที่ API ใช้
       *
       * เดิมพิมพ์ 5000 บนบิล 200 ได้ หน้าจอโชว์ยอด ฿0 (เพราะ recomputeBillTotals clamp อยู่แล้ว)
       * แต่ตอนส่งกลับส่ง 5000 ไป → API ตีกลับ ทั้งที่หน้าจอดูปกติดี
       * clamp ที่ช่องกรอกเลย ตัวเลขจะเด้งกลับเป็น 200 ให้เห็นทันทีว่าพิมพ์เกิน
       * (ส่วนลดรายบรรทัดถูก cap ที่ item.subtotal อยู่แล้ว — ตัวนี้ตกหล่น)
       */
      const items = (this.activeBill?.items || []).filter(it => !it.cancelled);
      const max = items.reduce((sum, it) => {
        const discount = Math.max(0, +it.discount || 0);
        return sum + Math.max(0, (+it.subtotal || 0) - discount);
      }, 0);
      const value = Math.max(0, Math.min(max, parseFloat(raw) || 0));
      this.activeBill.discount = +value.toFixed(2);
      this.recomputeBillTotals(this.activeBill);
    },
    onShippingPriceInput(raw) {
      const value = Math.max(0, parseFloat(raw) || 0);
      this.activeBill.shipping_price = value;
      this.recomputeBillTotals(this.activeBill);
    },
    // Recompute bill totals after any local edit (item/bill discount, shipping).
    recomputeBillTotals(bill) {
      if (!bill) return;

      // Use the API-provided subtotal as the active baseline (it already excludes
      // any partially-cancelled portion). Local discount applies on top.
      bill.items.forEach(it => {
        if (it.cancelled) return;
        const itemDiscount = Math.max(0, +it.discount || 0);
        const baseline = +it.subtotal || 0;
        it.total = +Math.max(0, baseline - itemDiscount).toFixed(2);
      });

      const activeItems = bill.items.filter(i => !i.cancelled);
      const subtotal = +activeItems.reduce((s, i) => s + (i.total || 0), 0).toFixed(2);
      const billDiscount = Math.max(0, +bill.discount || 0);
      const shippingPrice = Math.max(0, +bill.shipping_price || 0);
      const afterDiscount = Math.max(0, subtotal - billDiscount);
      const serviceChargeRate = +bill.service_charge_rate || 0;
      const serviceCharge = +(afterDiscount * serviceChargeRate / 100).toFixed(2);

      // Vatable / exempt portions derived from items' tax_status, then bill discount
      // is distributed proportionally between the two.
      let vatableTotal = 0;
      let exemptTotal = 0;
      activeItems.forEach(it => {
        if (it.tax_status === 'Vatable') vatableTotal += (it.total || 0);
        else exemptTotal += (it.total || 0);
      });
      if (subtotal > 0 && billDiscount > 0) {
        const vShare = vatableTotal / subtotal;
        vatableTotal = Math.max(0, +(vatableTotal - billDiscount * vShare).toFixed(2));
        exemptTotal = Math.max(0, +(exemptTotal - billDiscount * (1 - vShare)).toFixed(2));
      }

      const taxPolicy = bill.tax_policy;
      const vatRate = +bill.vat_rate || 0;
      let vat = 0;
      if (vatableTotal > 0 && vatRate > 0) {
        if (taxPolicy === 'Exclusive') {
          vat = +(vatableTotal * vatRate / 100).toFixed(2);
        } else if (taxPolicy === 'Inclusive') {
          vat = +(vatableTotal - vatableTotal / (1 + vatRate / 100)).toFixed(2);
        }
      }

      let grandTotal = subtotal - billDiscount + serviceCharge + shippingPrice;
      if (taxPolicy === 'Exclusive') grandTotal += vat;

      bill.subtotal = subtotal;
      bill.service_charge = serviceCharge;
      bill.vatable_total = vatableTotal;
      bill.exempt_total = exemptTotal;
      bill.vat = vat;
      bill.grand_total = +Math.max(0, grandTotal).toFixed(2);
    },
    // --- API mapping ---
    async onAttachMember(member) {
      if (!this.sessionToken || !this.activeBill?.id) return;
      try {
        // per-bill: ผูก member กับ "บิลนี้" เท่านั้น (ไม่กระทบ session/บิลอื่น)
        await Sessions.setBillCustomer(this.sessionToken, this.activeBill.id, {
          customer_id: member.id,
          customer_name: member.fullname,
          telephone: member.telephone,
        });
        await this.fetchBills();
        this.$toast.success("<B>" + this.$t('Success') + "</B>\n" + this.$t('Member attached.'));
      } catch (err) {
        this.$toast.warn(err?.response?.data?.message || this.$t('Failed to attach member'));
      }
    },
    async onDetachMember() {
      if (!this.sessionToken || !this.activeBill?.id) return;
      try {
        await Sessions.setBillCustomer(this.sessionToken, this.activeBill.id, { customer_id: null });
        await this.fetchBills();
      } catch (err) {
        this.$toast.warn(err?.response?.data?.message);
      }
    },
    // ของแถมเปลี่ยน (claim/cancel/restock/redeem) → รีเฟรชบิลเงียบ + แจ้ง host (CartPanel)
    // ให้รีเฟรชข้อมูลออเดอร์ของ session ด้วย — บรรทัด reward ฿0 จะโผล่ในรายการที่ส่งแล้วทันที
    onRewardChanged() {
      this.fetchBills(true);
      this.$emit('orders-changed');
    },
    // silent = รีเฟรชเงียบหลังแลก/รับ/ยกเลิกของแถม: ไม่ขึ้น skeleton + คงบิลที่เปิดอยู่ (ไม่เด้งกลับบิลแรก)
    async fetchBills(silent = false) {
      if (!this.sessionToken) return;
      if (!silent) this.loading = true;
      const prevActiveId = this.bills?.[this.activeBillIndex]?.id;
      try {
        const res = await Sessions.bills(this.sessionToken);
        const list = res.data?.data ?? res.data ?? [];
        this.bills = list.map(b => this.mapBillFromApi(b));
        // payment_methods sits at the response root (branch-scoped, not per-bill).
        this.paymentMethods = Array.isArray(res.data?.payment_methods)
          ? res.data.payment_methods
          : [];
        if (this.bills.length === 0) {
          this.bills = [this.emptyBill()];
        }
        // Default to the first unpaid bill, otherwise the first bill.
        const firstUnpaid = this.bills.findIndex(b => !b.paid);
        const keepIndex = silent && prevActiveId ? this.bills.findIndex(b => b.id === prevActiveId) : -1;
        this.activeBillIndex = keepIndex >= 0 ? keepIndex : (firstUnpaid >= 0 ? firstUnpaid : 0);
      } catch (err) {
        if (err?.response?.data?.message) {
          this.$toast.warn(err.response.data.message);
        } else {
          this.$toast.error('Failed to load bills');
        }
        this.bills = [this.emptyBill()];
      } finally {
        if (!silent) this.loading = false;
      }
      // โหลดแต้มระดับ session ไว้โชว์+แลกเมื่อยังไม่มีบิลจริง
      this.fetchSessionLoyalty();
    },
    async fetchSessionLoyalty() {
      if (!this.sessionToken) { this.sessionLoyalty = null; return; }
      try {
        const res = await Sessions.loyalty(this.sessionToken);
        this.sessionLoyalty = res.data?.data ?? res.data ?? null;
      } catch (err) {
        this.sessionLoyalty = null;
      }
    },
    mapBillFromApi(b) {
      const items = (b.items || []).map(it => {
        // `quantity` is the originally-ordered amount; `cancelled_quantity` is how many of
        // those have since been cancelled. The active billable count is the difference.
        const quantity = parseInt(it.quantity) || 0;
        const cancelledQuantity = parseFloat(it.cancelled_quantity) || 0;
        const refundedQuantity = parseFloat(it.refunded_quantity) || 0;
        const fullyCancelled = cancelledQuantity > 0 && cancelledQuantity >= quantity;
        // API: `unit_price` = base item price; `price` = per-unit price including options.
        // Internally we keep both — `unit_price` drives totals (price × qty = subtotal),
        // while `base_price` is shown separately so options add-ons stay visible.
        const basePrice = parseFloat(it.unit_price ?? it.price) || 0;
        const pricePerUnit = parseFloat(it.price) || 0;
        return {
          _source_id: it.id,
          name: it.name,
          quantity,
          base_price: basePrice,
          unit_price: pricePerUnit,
          subtotal: parseFloat(it.subtotal) || 0,
          discount: parseFloat(it.discount) || 0,
          total: parseFloat(it.total) || 0,
          remark: it.remark || '',
          tax_status: it.tax_status || '',
          cancelled_quantity: cancelledQuantity,
          cancelled_amount: parseFloat(it.cancelled_amount) || 0,
          cancelled_reason: it.cancelled_reason || '',
          cancelled: fullyCancelled,
          refunded_quantity: refundedQuantity,
          refunded_amount: parseFloat(it.refunded_amount) || 0,
          refund_reason: it.refund_reason || '',
          is_refunded: !!it.is_refunded,
          is_reward: !!it.is_reward,
          modifiers: (it.options || []).map(o => ({
            name: o.name,
            price: parseFloat(o.price) || 0,
          })),
        };
      });

      // `b.sub_total` is the gross sum of item subtotals (before per-item discounts).
      // Use the net sum of item totals instead so grand_total matches the per-item
      // discounts already reflected in each `item.total` — consistent with
      // recomputeBillTotals. Falls back to sub_total when there are no items.
      const grossSubtotal = parseFloat(b.sub_total) || 0;
      const subtotal = items.length
        ? +items.filter(i => !i.cancelled).reduce((s, i) => s + (i.total || 0), 0).toFixed(2)
        : grossSubtotal;
      const discount = parseFloat(b.discount) || 0;
      const serviceChargeAmount = parseFloat(b.service_charge_amount) || 0;
      const serviceChargeRate = parseFloat(b.service_charge_rate) || 0;
      const vatableTotal = parseFloat(b.vatable_total) || 0;
      const vatRate = parseFloat(b.vat_rate) || 0;
      const shippingPrice = parseFloat(b.shipping_price) || 0;
      const taxPolicy = b.tax_policy || 'No Tax';

      // VAT amount: only relevant when there's a vatable portion.
      let vatAmount = 0;
      if (vatableTotal > 0 && vatRate > 0) {
        if (taxPolicy === 'Exclusive') {
          vatAmount = vatableTotal * vatRate / 100;
        } else if (taxPolicy === 'Inclusive') {
          // VAT already in subtotal — display only.
          vatAmount = vatableTotal - vatableTotal / (1 + vatRate / 100);
        }
      }

      // Grand total — for Inclusive/No Tax the VAT is either absent or already in subtotal.
      let grandTotal = subtotal - discount + serviceChargeAmount + shippingPrice;
      if (taxPolicy === 'Exclusive') grandTotal += vatAmount;

      return {
        id: b.id,
        bill_no: b.bill_no,
        table_name: b.table_name,
        customer_name: b.customer_name,
        customer_phone: b.customer_phone,
        // loyalty panel ฝังมากับ bills response (เส้นเดียว) — LoyaltyBillPanel ใช้ผ่าน prop
        loyalty: b.loyalty || null,
        items,
        guests: parseInt(b.guests) || 0,
        subtotal,
        discount,
        discount_reason: b.discount_reason || '',
        service_charge: serviceChargeAmount,
        service_charge_rate: serviceChargeRate,
        vat: vatAmount,
        vat_rate: vatRate,
        vatable_total: vatableTotal,
        exempt_total: parseFloat(b.exempt_total) || 0,
        shipping_price: shippingPrice,
        tax_policy: taxPolicy,
        grand_total: grandTotal,
        payment_status: b.payment_status || 'unpaid',
        paid: b.payment_status === 'paid',
        refunded_amount: parseFloat(b.refunded_amount) || 0,
        refund_status: b.refund_status || 'none',
        refund_reason: b.refund_reason || '',
        voided_at: b.voided_at || '',
        void_reason: b.void_reason || '',
        closed_at: b.closed_at || '',
        created_by: b.created_by || '',
        created_at: b.created_at || '',
        updated_at: b.updated_at || '',
        table_name: b.table_name || '',
        customer_name: b.customer_name || '',
        customer_phone: b.customer_phone || '',
      };
    },
    // --- Local-only bill construction (used for split/merge before backend support) ---
    makeLocalBillNo() {
      const now = new Date();
      const y = now.getFullYear();
      const m = String(now.getMonth() + 1).padStart(2, '0');
      const d = String(now.getDate()).padStart(2, '0');
      this.localBillCounter += 1;
      const seq = String(this.localBillCounter).padStart(4, '0');
      return `LOCAL-${y}${m}${d}-${seq}`;
    },
    calcBillTotals(items, rates) {
      const activeItems = items.filter(i => !i.cancelled);
      const subtotal = activeItems.reduce((sum, i) => sum + (i.total || 0), 0);
      const discount = 0;
      const afterDiscount = subtotal - discount;
      const serviceCharge = +(afterDiscount * (rates.service_charge_rate || 0) / 100).toFixed(2);
      let vat = 0;
      let grandTotal = afterDiscount + serviceCharge;
      if (rates.tax_policy === 'Exclusive') {
        vat = +((afterDiscount + serviceCharge) * (rates.vat_rate || 0) / 100).toFixed(2);
        grandTotal += vat;
      } else if (rates.tax_policy === 'Inclusive' && rates.vat_rate > 0) {
        // Display-only: VAT already inside subtotal.
        vat = +(afterDiscount - afterDiscount / (1 + rates.vat_rate / 100)).toFixed(2);
      }
      return { subtotal, discount, service_charge: serviceCharge, vat, grand_total: grandTotal };
    },
    makeLocalBill(items, sourceBill) {
      const rates = {
        service_charge_rate: sourceBill?.service_charge_rate || 0,
        vat_rate: sourceBill?.vat_rate || 0,
        tax_policy: sourceBill?.tax_policy || 'No Tax',
      };
      const totals = this.calcBillTotals(items, rates);
      return {
        ...this.emptyBill(),
        id: 'local-' + Date.now() + '-' + this.localBillCounter,
        bill_no: this.makeLocalBillNo(),
        items,
        ...rates,
        ...totals,
        created_by: sourceBill?.created_by || '',
      };
    },
    async onSplitConfirmed(payload) {
      // payload is the API body: { items: [{ id, quantity }] }
      if (!payload?.items?.length) {
        this.currentView = 'bill';
        return;
      }
      const billId = this.activeBill?.id;
      // Guard: the merge flow can produce local-only bills with non-numeric ids.
      // The API only accepts persisted bill ids.
      if (!billId || String(billId).startsWith('local-')) {
        this.$toast.error(this.$t('This bill cannot be split yet — please reload.'));
        return;
      }

      this.splitting = true;
      try {
        const res = await Sessions.splitBill(this.sessionToken, billId, { items: payload.items });
        const message = res?.data?.data?.message || res?.data?.message || this.$t('Split Bill Success');
        this.$toast.success(message);

        // Backend creates the new bill and recalculates totals — refetch to sync.
        await this.fetchBills();
        // Land back on the (now reduced) original bill if it still exists, else
        // fall back to the first unpaid bill picked by fetchBills.
        const sourceIdx = this.bills.findIndex(b => b.id === billId);
        if (sourceIdx >= 0) this.activeBillIndex = sourceIdx;
        this.currentView = 'bill';
      } catch (err) {
        // Surface the specific 422 reason when present (invalid ids / qty / duplicates).
        const msg = err?.response?.data?.message
          || err?.response?.data?.data?.message
          || this.$t('Failed to split bill');
        this.$toast.error(msg);
        // Stay on the split view so the user can adjust without losing input.
      } finally {
        this.splitting = false;
      }
    },
    async onMergeConfirmed(payload) {
      // payload is the API body: { ids: [...] } where ids[0] is the target.
      const ids = Array.isArray(payload?.ids) ? payload.ids : [];
      if (ids.length < 2) {
        this.currentView = 'bill';
        return;
      }
      // Guard against leftover local-only bills (carry non-numeric ids that
      // the backend can't resolve).
      const hasLocal = ids.some(id => String(id).startsWith('local-'));
      if (hasLocal) {
        this.$toast.error(this.$t('Some bills cannot be merged yet — please reload.'));
        return;
      }

      const targetId = ids[0];
      this.merging = true;
      try {
        const res = await Sessions.mergeBill(this.sessionToken, { ids });
        const message = res?.data?.data?.message || res?.data?.message || this.$t('Merge Bill Success');
        this.$toast.success(message);

        // Backend recalculates the target bill and soft-deletes the merged-in
        // ones — refetch to sync, then land on the target.
        await this.fetchBills();
        const targetIdx = this.bills.findIndex(b => b.id === targetId);
        if (targetIdx >= 0) this.activeBillIndex = targetIdx;
        this.currentView = 'bill';
      } catch (err) {
        // Surface 422 reasons (invalid ids / duplicates / tax mismatch / extra keys).
        const msg = err?.response?.data?.message
          || err?.response?.data?.data?.message
          || this.$t('Failed to merge bills');
        this.$toast.error(msg);
        // Keep the user on the merge view so they can adjust selection.
      } finally {
        this.merging = false;
      }
    },
    async onPaymentConfirmed(payload) {
      // payload is the API body: { items?, discount?, payment: [...] }
      if (!payload?.payment?.length) return;
      const billId = this.activeBill?.id;
      if (!billId || String(billId).startsWith('local-')) {
        this.$toast.error(this.$t('This bill cannot accept payment yet — please reload.'));
        return;
      }

      this.paying = true;
      try {
        const res = await Sessions.payBill(this.sessionToken, billId, payload);
        const message = res?.data?.data?.message || res?.data?.message || this.$t('Payment Success');
        this.$toast.success('<b>' + this.$t('Success') + '</b> ' + message);

        // Earn happens server-side at payment — surface the points the API reports.
        // FE never computes points; it only reads what the response carries.
        this.showLoyaltyEarnToast(res?.data?.data ?? res?.data ?? {});

        // Notify external listeners (kept for back-compat with existing consumers).
        this.$emit('paid', { bill: this.activeBill, body: payload });

        // Auto-print the receipt on successful payment; open the cash drawer when
        // any tender was cash. Done before fetchBills() so activeBill still points
        // at the just-paid bill. handlePrint swallows its own errors, so a print
        // failure never disrupts the payment flow.
        // payload.payment items are { id, payment_method_id, amount } with no
        // `type`; map id (= branch_payment_method.id) back to paymentMethods to
        // see whether any tender was cash.
        const cashIds = new Set(
          (this.paymentMethods || []).filter(m => m && m.type === 'cash').map(m => m.id)
        );
        const paidCash = (payload.payment || []).some(p => p && cashIds.has(p.id));
        // `auto: true` lets the branch's auto_print_bill setting suppress this
        // post-payment print (the cashier can still print manually).
        await this.handlePrint({ cashDrawer: paidCash, webusbOnly: true, auto: true });

        // Backend updated payment_status / closed_at / totals — refetch to sync.
        await this.fetchBills();

        // Decide where to land based on the refreshed state of the paid bill.
        const paidBill = this.bills.find(b => b.id === billId);
        const allPaid = this.bills.length > 0 && this.bills.every(b => b.paid);
        if (allPaid) {
          setTimeout(() => {
            $(this.$refs.offcanvas).offcanvas('hide');
          }, 300);
          return;
        }
        if (paidBill && !paidBill.paid) {
          // Partial payment — stay on the same bill so the cashier can finish it.
          const idx = this.bills.findIndex(b => b.id === billId);
          if (idx >= 0) this.activeBillIndex = idx;
        } else {
          // Bill fully paid but other unpaid bills remain — jump to the next one.
          const nextUnpaid = this.bills.findIndex(b => !b.paid);
          if (nextUnpaid >= 0) this.activeBillIndex = nextUnpaid;
        }
        this.currentView = 'bill';
      } catch (err) {
        // Surface backend message (422 validation or 500 overpaid).
        const msg = err?.response?.data?.message
          || err?.response?.data?.data?.message
          || this.$t('Failed to process payment');
        this.$toast.error(msg);
        // Stay on the payment view so the cashier can adjust without losing input.
      } finally {
        this.paying = false;
      }
    },

    /**
     * ปิดบิลยอด ฿0 — ไม่ต้องรับเงิน ส่ง payment ว่าง
     * (แลกของแถมล้วน · โปรลด 100% · แคชเชียร์ใส่ส่วนลดเท่ายอดบิลพอดี)
     *
     * ⚠️ ต้องส่งส่วนลด/ค่าส่งที่แคชเชียร์เพิ่งคีย์ไปด้วยเสมอ (buildPrintBody)
     *    ส่วนลดพวกนี้ยังอยู่แค่ในหน้าจอ ยังไม่ได้บันทึกลงบิล — ถ้าส่งแค่ { payment: [] }
     *    ฝั่ง API จะเห็นยอดเดิม (เช่น 200) แล้วตอบ "Payment is required."
     *    ทั้งที่หน้าจอโชว์ยอดต้องจ่าย ฿0 อยู่ → ปิดบิลไม่ได้เลย
     */
    async closeFreeBill() {
      const billId = this.activeBill?.id;
      if (!this.sessionToken || !billId || String(billId).startsWith('local-')) return;

      const result = await this.$swal({
        title: this.$t('Close this ฿0 bill?'),
        text: this.$t('No payment will be recorded.'),
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: this.$t('Close Bill'),
        cancelButtonText: this.$t('Close'),
        confirmButtonColor: '#198754',
      });
      if (!result.isConfirmed) return;

      this.paying = true;
      try {
        const body = { ...this.buildPrintBody(this.activeBill), payment: [] };
        const res = await Sessions.payBill(this.sessionToken, billId, body);
        const message = res?.data?.data?.message || res?.data?.message || this.$t('Bill closed.');
        this.$toast.success('<b>' + this.$t('Success') + '</b> ' + message);
        this.showLoyaltyEarnToast(res?.data?.data ?? res?.data ?? {});
        this.$emit('paid', { bill: this.activeBill, body });

        await this.fetchBills();
        const allPaid = this.bills.length > 0 && this.bills.every(b => b.paid);
        if (allPaid) {
          setTimeout(() => { $(this.$refs.offcanvas).offcanvas('hide'); }, 300);
          return;
        }
        const nextUnpaid = this.bills.findIndex(b => !b.paid);
        if (nextUnpaid >= 0) this.activeBillIndex = nextUnpaid;
        this.currentView = 'bill';
      } catch (err) {
        const msg = err?.response?.data?.message
          || err?.response?.data?.data?.message
          || this.$t('Failed to process payment');
        this.$toast.error(msg);
      } finally {
        this.paying = false;
      }
    },

    // Read earned/remaining points from the payment response (two possible shapes:
    // nested `loyalty: { earned_points, balance_after }` or flat fields). FE only
    // displays what the server returns.
    showLoyaltyEarnToast(data) {
      const loyalty = data?.loyalty;
      const earned = loyalty ? loyalty.earned_points : data?.points_earned;
      const balance = loyalty ? loyalty.balance_after : data?.points_balance;
      if (earned == null && balance == null) return;
      const earnedTxt = (parseInt(earned) || 0).toLocaleString();
      const balanceTxt = (parseInt(balance) || 0).toLocaleString();
      this.$toast.info(
        '<b>' + this.$t('Earned') + ' ' + earnedTxt + ' ' + this.$t('pts') + '</b><br>' +
        this.$t('Balance') + ': ' + balanceTxt + ' ' + this.$t('pts')
      );
    },
    async handlePrint(options) {
      // `options.cashDrawer` opens the till (WebUSB path only). Guarded so a direct
      // @click — which passes the DOM event as `options` — doesn't trip it.
      const cashDrawer = options?.cashDrawer === true;
      // Auto-print after payment passes webusbOnly: print only if a real (WebUSB)
      // printer is connected, and never fall back to a PDF dialog — on mobile / when
      // there's no printer the customer often doesn't want a receipt.
      const webusbOnly = options?.webusbOnly === true;
      // `auto` marks the automatic post-payment print, which the branch's
      // auto_print_bill setting can suppress. Manual prints pass auto=false.
      const auto = options?.auto === true;
      const billId = this.activeBill?.id;
      if (!billId || String(billId).startsWith('local-')) {
        if (!webusbOnly) this.$toast.error(this.$t('This bill cannot be printed yet — please reload.'));
        return;
      }
      if (webusbOnly && !escposIsSupported()) return; // no printer → skip silently (no PDF)
      this.printing = true;

      const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
      // Reserve the popup inside the user gesture so iOS Safari doesn't block it.
      // Skip it when WebUSB will handle the print (desktop Chromium) — no popup needed.
      const popup = (isMobile && !escposIsSupported()) ? window.open('', '_blank') : null;

      try {
        // Send the cashier's draft edits (item/bill discount, shipping) so the
        // backend returns server-computed totals (incl. tax) for the receipt. We
        // can't compute these client-side reliably — the session payload carries
        // no resolved tax breakdown and `recomputeBillTotals` mis-cases tax_policy.
        // This is a preview-only POST; the bill isn't mutated until payment.
        const res = await Bills.print(billId, this.buildPrintBody(this.activeBill));
        const apiBill = res.data?.data || {};
        const branchConfig = res.data?.filter?.branch_config || {};
        const configuration = await preparePrintConfig(branchConfig);
        // ค่าภาษีเป็นของร้าน ไม่ใช่ของสาขา (api/docs/vat-compliance.md §2)
        const shopTax = res.data?.filter?.shop_tax || {};
        // Branch opted out of auto-printing the receipt after payment — stop
        // here (manual prints pass auto=false and are unaffected). Default ON.
        // The cash-drawer kick is normally piggybacked on the print job, so when
        // auto-print is off we still fire it standalone for a cash payment.
        // openCashDrawer() no-ops on a device set up without a drawer.
        if (auto && configuration.auto_print_bill === false) {
          if (cashDrawer) {
            try { await openCashDrawer({ promptIfMissing: false }); } catch (e) { console.warn('Cash drawer kick failed:', e); }
          }
          return;
        }
        const bill = this.normalizePrintBill(apiBill);

        // PromptPay QR — only when the branch toggled it on AND a method with
        // a promptpay_no exists in the session's payment_methods.
        const promptpayMethod = configuration.show_qrcode_promptpay
          ? (this.paymentMethods || []).find(m => m && m.promptpay_no) || null
          : null;
        const promptpayQrDataUrl = promptpayMethod
          ? await buildPromptPayQrImage(promptpayMethod, bill.grand_total)
          : '';
        const reviewQrDataUrl = (configuration.show_qrcode_review && configuration.review_url)
          ? await buildReviewQrImage(configuration.review_url)
          : '';

        const docOpts = {
          bill,
          configuration,
          shopTax,
          promptpayQrDataUrl,
          reviewQrDataUrl,
          promptpayMethod,
          fmt: (v) => this.$storeIndex.formatThaiCurrency(v),
          t: (s) => this.$t(s)
        };

        // Direct WebUSB raster print — cuts exactly under the receipt (no ~7cm
        // driver tail). Build without short-page padding. On unsupported/failure,
        // fall back to the PDF print path below.
        if (escposIsSupported()) {
          try {
            const canvas = await renderDocDefToCanvas(
              buildReceiptDocDefinition({ ...docOpts, padShortPage: false }),
              { widthPx: dotsForPaperWidth(configuration.bill_paper_width) }
            );
            // cashDrawer = "this is a cash payment"; printCanvas only kicks when
            // this device is set up with a drawer (printer settings).
            // promptIfMissing only on a print the cashier actually clicked — the
            // automatic post-payment one must never raise a USB chooser mid-shift.
            await printCanvas(canvas, {
              cashDrawer,
              promptIfMissing: !auto
            });
            return;
          } catch (e) {
            console.warn('WebUSB bill print failed:', e);
            // Auto-print: a real printer is present but failed — let the cashier
            // know, but don't pop a PDF dialog.
            if (webusbOnly) { this.$toast.warn(this.$t('Receipt not printed')); return; }
          }
        }
        await printPdf(buildReceiptDocDefinition(docOpts), { popup });
      } catch (e) {
        if (popup) popup.close();
        console.error('Bill print error:', e);
        this.$toast.error('Print failed: ' + (e.message || e));
      } finally {
        this.printing = false;
      }
    },
    // Build the preview body for POST /bills/{id}/print — the cashier's in-flight
    // discount/shipping edits, in the same shape as the payment body (minus
    // `payment`). The backend echoes back fully-computed totals for the receipt.
    buildPrintBody(bill) {
      const round2 = (v) => parseFloat((parseFloat(v) || 0).toFixed(2));
      const items = (bill?.items || [])
        .filter(it => !it.cancelled && it._source_id != null)
        .map(it => ({ id: it._source_id, discount: round2(it.discount) }));
      const body = {
        discount: round2(bill?.discount),
        shipping: round2(bill?.shipping_price),
      };
      if (items.length > 0) body.items = items;
      return body;
    },
    // Map the POST /bills/{id}/print payload → normalized shape for billReceipt.
    // Uses server-computed totals (total_vat / total) so the tax breakdown prints
    // correctly regardless of tax_policy casing.
    normalizePrintBill(api) {
      const toNum = (v) => {
        const n = parseFloat(v);
        return Number.isFinite(n) ? n : 0;
      };
      return {
        bill_no:             api.bill_no,
        // '' unless the API says this receipt is the abbreviated tax invoice — always ''
        // on this screen in practice (check-bill preview happens before payment)
        abbreviated_tax_invoice_no: api.abbreviated_tax_invoice_no || '',
        // ยังไม่จ่าย = ใบแจ้งรายการ ไม่ใช่เอกสารภาษี (billReceipt.js เปลี่ยนหัวบิลให้)
        payment_status: api.payment_status || '',
        // เลขประจำเครื่องจากสรรพากร + ช่องทางขาย — ต้องพิมพ์บนใบกำกับอย่างย่อ
        // (api/docs/vat-compliance.md D10) ว่างได้ทั้งคู่
        pos_device_code: api.pos_device_code || '',
        delivery_platform: api.delivery_platform || '',
        // วันที่ปิดการขาย — ใช้เป็นวันที่บนใบกำกับภาษี (พิมพ์ซ้ำต้องได้วันเดิม)
        closed_at:           api.closed_at || '',
        table_name:          api.table_name,
        customer_name:       api.customer_name,
        customer_phone:      api.customer_phone,
        tax_policy:          api.tax_policy,
        subtotal:            toNum(api.sub_total),
        discount:            toNum(api.discount),
        shipping_price:      toNum(api.shipping_price),
        service_charge:      toNum(api.service_charge_amount),
        service_charge_rate: toNum(api.service_charge_rate),
        total_before_vat:    toNum(api.total_before_vat),
        vat:                 toNum(api.total_vat),
        vat_rate:            toNum(api.vat_rate),
        grand_total:         toNum(api.total),
        // Loyalty points (server-computed) — printed when present. Supports the
        // nested `loyalty` object or flat fields on the print response.
        loyalty: api.loyalty
          ? api.loyalty
          : (api.points_earned != null || api.points_balance != null
              ? { earned_points: api.points_earned, balance_after: api.points_balance }
              : null),
        items: (api.items || []).map(it => ({
          name:               it.name,
          unit_price:         toNum(it.unit_price),
          quantity:           toNum(it.quantity),
          cancelled_quantity: toNum(it.cancelled_quantity),
          subtotal:           toNum(it.subtotal),
          discount:           toNum(it.discount),
          total:              toNum(it.total),
          remark:             it.remark || '',
          is_reward:          !!it.is_reward,
          modifiers: (it.options || []).map(o => ({ name: o.name, price: parseFloat(o.price) || 0 }))
        }))
      };
    }
  },
  async mounted() {
    $(this.$refs.offcanvas).offcanvas('show');
    $(this.$refs.offcanvas).on('hidden.bs.offcanvas', () => {
      this.$emit('close');
    });
    useScrollRestore(() => this.$refs.offcanvas?.querySelector('.offcanvas-body'));
    await this.fetchBills();
  }
};
</script>

<style scoped>
/* การ์ดสมาชิกที่ผูกกับบิล */
.member-card {
  background: linear-gradient(135deg, rgba(25, 135, 84, 0.10), rgba(25, 135, 84, 0.04));
  border: 1px solid rgba(25, 135, 84, 0.25);
}
.member-avatar {
  width: 40px;
  height: 40px;
  background: #198754;
  color: #fff;
  font-size: 1rem;
  box-shadow: 0 2px 6px rgba(25, 135, 84, 0.35);
}
.member-sub {
  font-size: 0.72rem;
  color: #198754;
  opacity: 0.8;
}
.member-remove {
  width: 32px;
  height: 32px;
  color: #dc3545;
  background: #fff;
  border: 1px solid rgba(220, 53, 69, 0.35);
  transition: all 0.15s ease;
}
.member-remove:hover {
  background: #dc3545;
  color: #fff;
}
/* fade ตอนผูก/เอาออก */
.member-fade-enter-active,
.member-fade-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}
.member-fade-enter-from,
.member-fade-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}
</style>
